import React, {useEffect, useState} from "react";
import Sidebar from "../components/Sidebar";
import AddButton from "../components/AddButton";

import {registerLocale} from "react-datepicker";
import {it} from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

import axios from "axios";
import {toast} from "react-toastify";
import {privateApi} from "../api";
import RefreshButton from "../components/RefreshButton";
import {colorMap, textColorMapTranslation} from "../service/colorService";

registerLocale("it", it);

// ---- TYPES ----
type StatusInfo = {
    id: string;
    name: string;
    color: string;
}

type StatusResponse = {
    status: string;
    code: number;
    message: string;
    statuses: StatusInfo[];
}

function Status() {
    localStorage.setItem("focus", "stati")
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [statuses, setStatuses] = useState<StatusInfo[]>([]);
    const [selectedStatusId, setSelectedStatusId] = useState("");
    const [selectedStatusName, setSelectedStatusName] = useState("");
    const [selectedStatusColor, setSelectedStatusColor] = useState("");
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [statusInput, setStatusInput] = useState("");
    const [statusColorInput, setStatusColorInput] = useState("stone");

    // ---- RESET ----
    const resetForm = () => {
        setStatusInput("");
    };

    const loadStatuses = async () => {
        setLoading(true);
        try {
            const res = await privateApi.get<StatusResponse>("/cr-order-manager/v1/status");

            setStatuses(res.data.statuses ?? []);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStatuses();
    }, []);

    // ---- SUBMIT ----
    const handleSubmit = async () => {
        if (statusInput.trim() === "") return toast.error("Inserisci uno stato valido");

        const payload = {
            status: statusInput,
            color: statusColorInput
        };

        try {
            await privateApi.post("/cr-order-manager/v1/status", payload);

            setIsModalOpen(false);
            resetForm();
            loadStatuses();
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        }
    };

    const handleEdit = async () => {
        if (selectedStatusName.trim() === "") return toast.error("Inserisci uno stato valido");

        const payload = {
            statusId: selectedStatusId,
            status: selectedStatusName,
            color: selectedStatusColor
        };

        try {
            await privateApi.patch("/cr-order-manager/v1/status", payload);

            setIsEditModalOpen(false);
            loadStatuses();
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        }
    };

    const handleDelete = async () => {
        if (selectedStatusName.trim() === "") return toast.error("Inserisci un brand valido");

        try {
            await privateApi.delete(`/cr-order-manager/v1/status/${selectedStatusId}`);

            setIsEditModalOpen(false);
            loadStatuses();
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        }
    };

    return (
        <>
            <Sidebar/>

            <main className="ml-64 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold">Stati</h1>
                    <div className="flex items-center gap-2">
                        <RefreshButton onClick={loadStatuses}/>
                        <AddButton
                            onClick={() => {
                                resetForm();
                                setIsModalOpen(true);
                            }}
                        />
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow p-5">

                        {/*Table*/}
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">

                                <thead className="text-gray-500 border-b">
                                <tr>
                                    <th className="py-2">ID</th>
                                    <th className="py-2">Nome</th>
                                    <th className="py-2">Colore</th>
                                </tr>
                                </thead>

                                <tbody className="divide-y">
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="py-6 text-center text-gray-500"
                                        >
                                            Caricamento...
                                        </td>
                                    </tr>
                                ) : statuses.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="py-6 text-center text-gray-500"
                                        >
                                            Nessuno stato trovato
                                        </td>
                                    </tr>
                                ) : (statuses.map((status: StatusInfo) => (
                                    <tr key={status.id} className="hover:bg-gray-50"
                                        onClick={() => {
                                            setSelectedStatusId(status.id);
                                            setSelectedStatusName(status.name);
                                            setSelectedStatusColor(status.color);
                                            setIsEditModalOpen(true);
                                        }}>
                                        <td className="py-2">{status.id}</td>
                                        <td className="py-2">{status.name}</td>
                                        <td className="py-2">
                                            <div
                                                className={`w-4 h-4 rounded-sm ${colorMap[status.color] ?? "bg-stone-600"}`}/>
                                        </td>
                                    </tr>
                                )))}

                                </tbody>

                            </table>
                        </div>

                    </div>
                </div>

                {isEditModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white rounded-xl p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">
                                Modifica Stato
                            </h2>

                            <div className="space-y-3">
                                <input className="w-full border p-2 rounded"
                                       placeholder="Nome stato"
                                       value={selectedStatusName}
                                       onChange={(e) => {
                                           setSelectedStatusName(e.target.value);
                                       }}/>
                                <select
                                    value={selectedStatusColor}
                                    onChange={(e) => {
                                        setSelectedStatusColor(e.target.value);
                                    }}

                                    className="w-full border p-2 rounded">
                                    {Object.keys(colorMap).map((key) => {
                                        return (<option key={key}
                                                        value={key}>{textColorMapTranslation[key].toUpperCase()}</option>)
                                    })}
                                </select>
                            </div>

                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={handleDelete}
                                    className="px-4 py-2 text-white bg-red-700 rounded"
                                >
                                    Elimina
                                </button>
                                <button
                                    onClick={() => {
                                        setIsEditModalOpen(false);
                                    }}
                                    className="px-4 py-2 bg-gray-200 rounded"
                                >
                                    Annulla
                                </button>

                                <button
                                    onClick={handleEdit}
                                    className="px-4 py-2 bg-blue-900 text-white rounded"
                                >
                                    Salva
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white rounded-xl p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">
                                Nuovo Stato
                            </h2>

                            <div className="space-y-3">
                                <input className="w-full border p-2 rounded"
                                       placeholder="Nome stato"
                                       value={statusInput}
                                       onChange={(e) => {
                                           setStatusInput(e.target.value);
                                       }}/>
                                <select
                                    value={statusColorInput}
                                    onChange={(e) => {
                                        setStatusColorInput(e.target.value);
                                    }}

                                    className="w-full border p-2 rounded">
                                    {Object.keys(colorMap).map((key) => {
                                        return (<option key={key}
                                                        value={key}>{textColorMapTranslation[key].toUpperCase()}</option>)
                                    })}
                                </select>
                            </div>

                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 bg-gray-200 rounded"
                                >
                                    Annulla
                                </button>

                                <button
                                    onClick={handleSubmit}
                                    className="px-4 py-2 bg-blue-900 text-white rounded"
                                >
                                    Salva
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </>
    )
        ;
}

export default Status;